import { useMemo } from 'react';
import { useAppSelector } from '@/app/hooks';
import { useGetConfigurationQuery, useGetMenuQuery } from '@/app/api/resourceApi';
import { selectTableSession } from '@/features/tableSession/tableSessionSlice';
import { useGetTableSessionQuery } from '@/features/tableSession/tableSessionApi';
import { splitCartByCourse } from '@/features/waiterTicket/commandesGrouping';
import type { DineInCourse } from '@/types';

/**
 * DF-0586 — the services a QR table is asked to choose between, in kitchen
 * order (e.g. ['Entrées', 'Plats', 'Desserts']), or null when there is
 * nothing to ask:
 *   - « envoi par service » off, or no service configured ;
 *   - a buffet session (SC-902 §1.9: no course split there) ;
 *   - the table already holds a service back — it chose « service par
 *     service » earlier, sale_api ranges this round against it by itself ;
 *   - the cart falls in a single service (nothing to split).
 * Same service resolution as the carnet (`splitCartByCourse`), so the
 * question matches what sale_api will actually do.
 */
export function serviceChoiceQuestion({
  enabled,
  courses,
  sessionMode,
  holdsAService,
  productIds,
  categoryIdByProduct,
}: {
  enabled: boolean;
  courses: DineInCourse[];
  sessionMode: 'ordering' | 'buffet' | undefined;
  holdsAService: boolean;
  productIds: string[];
  categoryIdByProduct: Map<string, string>;
}): string[] | null {
  if (!enabled || courses.length === 0 || sessionMode === 'buffet' || holdsAService) return null;
  const groups = splitCartByCourse(
    productIds.map((itemId) => ({ itemId })),
    courses,
    categoryIdByProduct,
  );
  return groups.length >= 2 ? groups.map((group) => group.label) : null;
}

/** DF-0586 — `serviceChoiceQuestion` wired to the live restaurant config, menu
 *  and table session (both already fetched/polled by the table pages: RTK
 *  Query dedupes these subscriptions). */
export function useServiceChoiceQuestion(restaurantUuid: string, productIds: string[]): string[] | null {
  const session = useAppSelector(selectTableSession);
  const guestToken = session?.guestToken ?? '';
  const { data: config } = useGetConfigurationQuery(restaurantUuid, { skip: !restaurantUuid });
  const { data: menu } = useGetMenuQuery(restaurantUuid, { skip: !restaurantUuid });
  const { data: current } = useGetTableSessionQuery(
    {
      restaurantUuid: session?.restaurantUuid ?? '',
      sessionUuid: session?.sessionUuid ?? '',
      guestToken,
    },
    { skip: !guestToken },
  );
  const categoryIdByProduct = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of menu?.items ?? []) map.set(item.id, item.categoryId);
    return map;
  }, [menu]);
  const key = productIds.join('|');
  return useMemo(
    () =>
      serviceChoiceQuestion({
        enabled: config?.dineInCoursesEnabled ?? false,
        courses: config?.dineInCourses ?? [],
        sessionMode: current?.mode ?? session?.mode,
        holdsAService: current?.holds_a_service ?? false,
        productIds,
        categoryIdByProduct,
      }),
    // productIds is compared by content (`key`), not identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, current?.mode, current?.holds_a_service, session?.mode, key, categoryIdByProduct],
  );
}
